import { useMemo, useState } from 'react';
import { Modal, Select, message } from 'antd';
import LocationCascade from '../../components/LocationCascade';
import TreeDropdown from '../../components/TreeDropdown';
import OrgNodeTree, { orgNodeLabels, keyId } from '../../components/OrgNodeTree';
import { useCreateUserMutation } from '../../features/users/usersApi';
import { useGetNodeTreeQuery } from '../../features/organizations/organizationsApi';
import { useGetRolesQuery } from '../../features/roles/rolesApi';
import { PlusIcon, TrashIcon } from '../../assets/icons';

/**
 * PROOF OF CONCEPT — spreadsheet-style bulk "Add Users" for ALL directories.
 *
 * Demonstrates the workflow Sampath's boss suggested (an in-app editable grid instead
 * of the Excel template round-trip): pick a directory, then type / paste-from-Excel into
 * the text cells and pick per-row Location / Role from the existing pickers. Columns adapt
 * to the directory exactly like the single Create-User form.
 *
 * NOTE: uses a lightweight in-house grid, NOT ReactGrid — MIT ReactGrid (v4) supports only
 * React ≤18 (and uses `findDOMNode`, removed in React 19); the React-19-ready ReactGrid v5
 * is commercial/alpha. The workflow is identical; only the grid engine differs.
 */

const DIRECTORIES = [
  { key: 'elected', label: 'Elected Representatives' },
  { key: 'bureaucracy', label: 'Government Officials' },
  { key: 'party', label: 'Party Representatives' },
  { key: 'organization', label: 'Organization Members' },
];
const hasDepartment = (d: string) => d === 'bureaucracy' || d === 'organization';
const isOrgDirectory = (d: string) => d === 'organization';
const isCascadeDirectory = (d: string) => d === 'elected' || d === 'bureaucracy';

interface Row {
  fullName: string;
  email: string;
  /** Comma-separated in the cell; split into an array on submit. */
  contact: string;
  /** Comma-separated designation tags. */
  designation: string;
  /** Comma-separated department tags (bureaucracy / organization). */
  department: string;
  /** EC node code (cascade) or org-node key "n:15" (party). */
  location: string;
  locationRank?: number;
  /** Selected role id (organization). */
  role?: number | '';
}

const emptyRow = (): Row => ({
  fullName: '',
  email: '',
  contact: '',
  designation: '',
  department: '',
  location: '',
  role: '',
});

const isEmail = (v: string) => /.+@.+\..+/.test(v.trim());
/** Normalize a phone to its 10-digit form, tolerating +91 / 91 / leading-0 and spaces. */
const normalizePhone = (v: string) => {
  let d = v.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return d;
};
const isPhone = (v: string) => /^\d{10}$/.test(normalizePhone(v));
/** Split a cell into trimmed values on comma OR newline (Alt+Enter pastes from Excel). */
const tags = (v: string) => v.split(/[,\n]/).map((s) => s.trim()).filter(Boolean);

function rowValid(r: Row, directory: string): boolean {
  if (!r.fullName.trim() || !isEmail(r.email) || !r.designation.trim()) return false;
  const phones = tags(r.contact);
  if (!phones.length || !phones.every(isPhone)) return false;
  if (hasDepartment(directory) && !r.department.trim()) return false;
  if (isOrgDirectory(directory)) return r.role !== '' && r.role != null;
  return !!r.location; // elected / bureaucracy / party need a location
}
const rowEmpty = (r: Row) =>
  !r.fullName.trim() &&
  !r.email.trim() &&
  !r.contact.trim() &&
  !r.designation.trim() &&
  !r.department.trim() &&
  !r.location &&
  (r.role === '' || r.role == null);

/* Per-field validation messages — shown under the offending cell. */
const nameErr = (r: Row) => (!r.fullName.trim() ? 'Required' : '');
const emailErr = (r: Row) =>
  !r.email.trim() ? 'Required' : !isEmail(r.email) ? 'Invalid email' : '';
const contactErr = (r: Row) => {
  const p = tags(r.contact);
  return !p.length ? 'Required' : !p.every(isPhone) ? 'Use 10-digit number(s)' : '';
};
const desigErr = (r: Row) => (!r.designation.trim() ? 'Required' : '');
const deptErr = (r: Row) => (!r.department.trim() ? 'Required' : '');
const locErr = (r: Row) => (!r.location ? 'Select a location' : '');
const roleErr = (r: Row) => (r.role === '' || r.role == null ? 'Select a role' : '');

/** Small red hint shown beneath a cell when it's invalid (once the row has content). */
function Err({ show, msg }: { show: boolean; msg: string }) {
  if (!show || !msg) return null;
  return <span className="mt-0.5 block text-[11px] font-medium text-red-500">{msg}</span>;
}

const CELL = 'w-full rounded-md border px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2';
const cellCls = (invalid: boolean) =>
  [
    CELL,
    invalid
      ? 'border-red-400 focus:ring-red-100'
      : 'border-[#e2e8f0] focus:border-brand focus:ring-brand/15',
  ].join(' ');

export default function BulkAddUsersModal({
  open,
  onClose,
  organization,
  onDone,
}: {
  open: boolean;
  onClose: () => void;
  /** The organization new users are created under (drives the location trees). */
  organization?: number;
  onDone?: () => void;
}) {
  const [directory, setDirectory] = useState('elected');
  const [rows, setRows] = useState<Row[]>(() => [emptyRow(), emptyRow(), emptyRow()]);
  const [createUser] = useCreateUserMutation();
  const [saving, setSaving] = useState(false);
  const [touched, setTouched] = useState(false);

  // Party location tree + role options (loaded for the org).
  const { data: orgTree } = useGetNodeTreeQuery(
    { organization: organization as number },
    { skip: organization == null || directory !== 'party' },
  );
  const orgNodeNameMap = useMemo(() => orgNodeLabels(orgTree ?? []), [orgTree]);
  const { data: rolesData } = useGetRolesQuery(
    { all: true },
    { skip: directory !== 'organization' },
  );
  const roleOptions = (rolesData?.items ?? []).map((r) => ({ value: r.id, label: r.name }));

  const showDept = hasDepartment(directory);
  const showRole = isOrgDirectory(directory);
  const showLocation = !isOrgDirectory(directory);
  const locationIsCascade = isCascadeDirectory(directory);

  const update = (i: number, patch: Partial<Row>) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  const addRow = () => setRows((rs) => [...rs, emptyRow()]);
  const removeRow = (i: number) =>
    setRows((rs) => {
      const next = rs.filter((_, idx) => idx !== i);
      return next.length ? next : [emptyRow()];
    });

  // Switching directory clears the fields whose meaning changes (location / role /
  // department), but keeps the plain text columns.
  const switchDirectory = (d: string) => {
    setDirectory(d);
    setRows((rs) => rs.map((r) => ({ ...r, location: '', locationRank: undefined, role: '', department: '' })));
  };

  // Paste a block from Excel: tab-separated columns → the text cells in order.
  const handlePaste = (startRow: number, e: React.ClipboardEvent) => {
    const text = e.clipboardData.getData('text');
    if (!text.includes('\t') && !text.includes('\n')) return;
    e.preventDefault();
    const lines = text.replace(/\r/g, '').split('\n').filter((l) => l.length > 0);
    const cols: (keyof Row)[] = [
      'fullName',
      'email',
      'contact',
      'designation',
      ...(showDept ? (['department'] as (keyof Row)[]) : []),
    ];
    setRows((rs) => {
      const next = [...rs];
      lines.forEach((line, li) => {
        const cells = line.split('\t');
        const target = startRow + li;
        if (!next[target]) next[target] = emptyRow();
        cells.forEach((c, ci) => {
          if (cols[ci]) next[target] = { ...next[target], [cols[ci]]: c.trim() };
        });
      });
      return next;
    });
  };

  const filled = rows.filter((r) => !rowEmpty(r));
  const validRows = filled.filter((r) => rowValid(r, directory));
  const invalidCount = filled.length - validRows.length;

  const handleSubmit = async () => {
    setTouched(true);
    if (!organization) {
      message.warning('No organization selected for these users.');
      return;
    }
    if (!validRows.length) {
      message.warning('Add at least one complete row.');
      return;
    }
    const skipped = filled.length - validRows.length;
    if (skipped) message.warning(`${skipped} incomplete row(s) skipped — fix the highlighted fields.`);
    setSaving(true);
    let created = 0;
    const failed: string[] = [];
    for (const r of validRows) {
      const base = {
        directory,
        fullName: r.fullName.trim(),
        email: r.email.trim(),
        contact: tags(r.contact).map(normalizePhone),
        designation: tags(r.designation),
        organization,
        active: true,
        ...(showDept ? { department: tags(r.department) } : {}),
      };
      const payload = isOrgDirectory(directory)
        ? { ...base, role: r.role === '' ? null : Number(r.role) }
        : isCascadeDirectory(directory)
          ? {
              ...base,
              location: r.location,
              ...(r.locationRank != null ? { ec_location_level_id: r.locationRank } : {}),
            }
          : { ...base, location: r.location ? keyId(r.location) : null };
      try {
        await createUser(payload).unwrap();
        created += 1;
      } catch {
        failed.push(r.email || r.fullName);
      }
    }
    setSaving(false);
    if (created) message.success(`${created} user(s) created.`);
    if (failed.length) message.error(`Failed: ${failed.join(', ')}`);
    if (created && !failed.length) {
      setRows([emptyRow(), emptyRow(), emptyRow()]);
      setTouched(false);
      onDone?.();
      onClose();
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title="Bulk Add Users"
      width={1120}
      okText={saving ? 'Saving…' : `Create ${validRows.length || ''} User(s)`}
      // Enabled once there's a row to submit + an org; clicking with incomplete rows
      // highlights the missing fields instead of silently doing nothing.
      okButtonProps={{ disabled: saving || !organization || !filled.length }}
      onOk={handleSubmit}
      confirmLoading={saving}
      destroyOnHidden
    >
      <div className="flex flex-col gap-3">
        {/* Directory selector — columns adapt to the choice. */}
        <div className="flex flex-wrap items-center gap-2">
          {DIRECTORIES.map((d) => (
            <button
              key={d.key}
              type="button"
              onClick={() => switchDirectory(d.key)}
              className={[
                'rounded-lg px-4 py-1.5 text-xs font-semibold transition-colors',
                directory === d.key
                  ? 'bg-[#2563eb] text-white'
                  : 'border border-[#e2e8f0] bg-white font-medium text-[#475569] hover:bg-[#f8fafc]',
              ].join(' ')}
            >
              {d.label}
            </button>
          ))}
        </div>

        <p className="text-sm text-[#64748b]">
          Type rows, or paste from Excel (Full Name → Email → Contact → Designation
          {showDept ? ' → Department' : ''}). Commas allow multiple contacts / tags.
        </p>

        {/* No overflow here: it would clip the Location / Role dropdown panels. */}
        <div className="rounded-lg border border-[#e2e8f0]">
          <table className="w-full border-collapse text-sm">
            <thead>
              <tr className="bg-[#eef4ff] text-left text-xs font-semibold text-[#64748b]">
                <th className="w-8 px-2 py-2">#</th>
                <th className="px-2 py-2">Full Name *</th>
                <th className="px-2 py-2">Email *</th>
                <th className="px-2 py-2">Contact *</th>
                <th className="px-2 py-2">Designation *</th>
                {showDept && <th className="px-2 py-2">Department *</th>}
                {showLocation && <th className="w-[240px] px-2 py-2">Location *</th>}
                {showRole && <th className="w-[200px] px-2 py-2">Role *</th>}
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => {
                // Highlight invalid cells live once a row has any content (not only after
                // a submit), so it's obvious which field is keeping the row "incomplete".
                const show = touched || !rowEmpty(r);
                return (
                  <tr key={i} className="border-t border-[#f1f5f9] align-top">
                    <td className="px-2 py-1.5 text-xs text-[#94a3b8]">{i + 1}</td>
                    <td className="px-2 py-1.5">
                      <input
                        value={r.fullName}
                        onChange={(e) => update(i, { fullName: e.target.value })}
                        onPaste={(e) => handlePaste(i, e)}
                        className={cellCls(show && !!nameErr(r))}
                        placeholder="Rajesh Kumar"
                      />
                      <Err show={show} msg={nameErr(r)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={r.email}
                        onChange={(e) => update(i, { email: e.target.value })}
                        onPaste={(e) => handlePaste(i, e)}
                        className={cellCls(show && !!emailErr(r))}
                        placeholder="rajesh@chunaav.gov.in"
                      />
                      <Err show={show} msg={emailErr(r)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={r.contact}
                        onChange={(e) => update(i, { contact: e.target.value })}
                        onPaste={(e) => handlePaste(i, e)}
                        className={cellCls(show && !!contactErr(r))}
                        placeholder="9876543210"
                      />
                      <Err show={show} msg={contactErr(r)} />
                    </td>
                    <td className="px-2 py-1.5">
                      <input
                        value={r.designation}
                        onChange={(e) => update(i, { designation: e.target.value })}
                        onPaste={(e) => handlePaste(i, e)}
                        className={cellCls(show && !!desigErr(r))}
                        placeholder="MLA, Whip"
                      />
                      <Err show={show} msg={desigErr(r)} />
                    </td>
                    {showDept && (
                      <td className="px-2 py-1.5">
                        <input
                          value={r.department}
                          onChange={(e) => update(i, { department: e.target.value })}
                          onPaste={(e) => handlePaste(i, e)}
                          className={cellCls(show && !!deptErr(r))}
                          placeholder="Revenue"
                        />
                        <Err show={show} msg={deptErr(r)} />
                      </td>
                    )}
                    {showLocation && (
                      <td className="px-2 py-1.5">
                        {locationIsCascade ? (
                          <LocationCascade
                            organization={organization}
                            value={r.location}
                            placeholder="Location"
                            invalid={show && !!locErr(r)}
                            onChange={(sel) =>
                              update(i, {
                                location: sel ? String(sel.value) : '',
                                locationRank: sel?.levelRank,
                              })
                            }
                          />
                        ) : (
                          <TreeDropdown
                            placeholder="Location"
                            invalid={show && !!locErr(r)}
                            valueLabel={r.location ? orgNodeNameMap[r.location] || r.location : ''}
                            onClear={() => update(i, { location: '' })}
                          >
                            {(close) => (
                              <OrgNodeTree
                                nodes={orgTree ?? []}
                                hideAreas
                                searchable
                                searchPlaceholder="Search hierarchy…"
                                selected={new Set(r.location ? [r.location] : [])}
                                onToggle={(key) => {
                                  update(i, { location: r.location === key ? '' : key });
                                  if (r.location !== key) close();
                                }}
                              />
                            )}
                          </TreeDropdown>
                        )}
                        <Err show={show} msg={locErr(r)} />
                      </td>
                    )}
                    {showRole && (
                      <td className="px-2 py-1.5">
                        <Select
                          className="w-full"
                          placeholder="Role"
                          showSearch
                          optionFilterProp="label"
                          status={show && !!roleErr(r) ? 'error' : undefined}
                          value={r.role === '' || r.role == null ? undefined : r.role}
                          onChange={(v) => update(i, { role: (v as number | undefined) ?? '' })}
                          options={roleOptions}
                        />
                        <Err show={show} msg={roleErr(r)} />
                      </td>
                    )}
                    <td className="px-2 py-1.5">
                      <button
                        type="button"
                        aria-label="Remove row"
                        onClick={() => removeRow(i)}
                        className="flex size-8 items-center justify-center rounded-lg text-[#64748b] transition-colors hover:bg-[#fee2e2] hover:text-[#dc2626]"
                      >
                        <TrashIcon className="size-4" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between">
          <button
            type="button"
            onClick={addRow}
            className="flex items-center gap-1.5 text-sm font-medium text-brand hover:underline"
          >
            <PlusIcon className="size-3.5" /> Add Row
          </button>
          <span className="text-xs text-[#64748b]">
            {validRows.length} ready{invalidCount ? ` · ${invalidCount} incomplete` : ''}
            {!organization ? ' · no organization selected' : ''}
          </span>
        </div>
      </div>
    </Modal>
  );
}
